import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useSearchParams } from 'react-router-dom';
import { patientsAPI, formsAPI, invoicesAPI, eventQuotesAPI, auditAPI } from '../lib/api';
import api from '../lib/api';
import { formatDateTime, downloadBlob } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Shield,
  Database,
  FileSpreadsheet,
  Download,
  Upload,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  Link,
  Unlink,
  Play,
  Pause,
  Loader2,
  AlertCircle,
  Users,
  FileText,
  Receipt,
  Calendar,
  Activity,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

// Dataset configurations
const DATASETS = [
  { 
    id: 'patients', 
    name: 'Patients', 
    icon: Users, 
    description: 'Patient demographics and medical info',
    fields: ['patient_id', 'first_name', 'last_name', 'date_of_birth', 'gender', 'phone', 'email', 'address', 'blood_type', 'allergies', 'active_problems', 'medications', 'insurance_info']
  },
  { 
    id: 'submissions', 
    name: 'Visits/Encounters', 
    icon: FileText, 
    description: 'Form submissions and clinical encounters',
    fields: ['id', 'form_name', 'patient_id', 'category', 'submitted_by_name', 'created_at', 'data']
  },
  { 
    id: 'clinical_notes', 
    name: 'Clinical Notes', 
    icon: Activity, 
    description: 'AI-generated and approved clinical notes',
    fields: ['id', 'patient_id', 'note_type', 'original_dictation', 'ai_generated_note', 'final_note', 'status', 'created_by_name', 'created_at']
  },
  { 
    id: 'invoices', 
    name: 'Invoices', 
    icon: Receipt, 
    description: 'Billing and invoice records',
    fields: ['invoice_number', 'patient_id', 'patient_name', 'line_items', 'subtotal', 'total_adjustments', 'total', 'status', 'created_at']
  },
  { 
    id: 'event_quotes', 
    name: 'Event Quote Requests', 
    icon: Calendar, 
    description: 'Event medical services inquiries',
    fields: ['reference_number', 'business_name', 'contact_name', 'email', 'phone', 'event_location', 'event_dates', 'expected_attendees', 'event_type', 'coverage_level', 'status', 'submitted_at']
  },
];

export default function DataExportPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('export');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState({});
  
  // Google connection state
  const [googleStatus, setGoogleStatus] = useState({ configured: false, connected: false });
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState('');
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  
  // Export configurations
  const [exportConfigs, setExportConfigs] = useState(
    DATASETS.reduce((acc, ds) => ({
      ...acc,
      [ds.id]: {
        enabled: true,
        sheetName: ds.name,
        selectedFields: ds.fields,
        exportMode: 'manual', // manual, scheduled, realtime
        lastExport: null,
        lastStatus: null,
      }
    }), {})
  );
  
  // Export logs
  const [exportLogs, setExportLogs] = useState([]);
  
  // Field mapping dialog
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDataset, setMappingDataset] = useState(null);

  useEffect(() => {
    // Check for Google OAuth callback
    const googleConnected = searchParams.get('google_connected');
    const googleError = searchParams.get('google_error');
    
    if (googleConnected === 'true') {
      toast.success('Google account connected successfully!');
      setActiveTab('google-sheets');
    }
    if (googleError) {
      toast.error(`Google connection failed: ${googleError}`);
    }
    
    // Fetch Google status
    fetchGoogleStatus();
  }, [searchParams]);

  const fetchGoogleStatus = async () => {
    try {
      const response = await api.get('/integrations/google/status');
      setGoogleStatus(response.data);
    } catch (err) {
      // Not connected or error - that's fine
      setGoogleStatus({ configured: false, connected: false });
    }
  };

  // Only Admin can access this page
  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-slate-500 opacity-20" />
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-slate-500">
          Only Administrators can access the Data Export Center.
        </p>
      </div>
    );
  }

  const handleConnectGoogle = async () => {
    setLoadingGoogle(true);
    try {
      const response = await api.get('/oauth/google/login');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (err) {
      if (err.response?.status === 400) {
        toast.info('Google OAuth is not configured. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to enable real integration.');
        // Show mock connection for demo purposes
        setGoogleStatus(prev => ({ ...prev, connected: true }));
      } else {
        toast.error('Failed to initiate Google connection');
      }
    }
    setLoadingGoogle(false);
  };

  const handleDisconnectGoogle = async () => {
    try {
      await api.post('/oauth/google/disconnect');
      setGoogleStatus(prev => ({ ...prev, connected: false }));
      toast.success('Disconnected from Google');
    } catch (err) {
      // Mock disconnect
      setGoogleStatus(prev => ({ ...prev, connected: false }));
      toast.success('Disconnected from Google');
    }
  };

  const handleExportToSheets = async (datasetId) => {
    if (!spreadsheetUrl) {
      toast.error('Please enter a spreadsheet ID or URL');
      return;
    }
    
    setExporting(prev => ({ ...prev, [`sheets_${datasetId}`]: true }));
    
    try {
      // Extract spreadsheet ID from URL if full URL provided
      let spreadsheetId = spreadsheetUrl;
      if (spreadsheetUrl.includes('spreadsheets/d/')) {
        spreadsheetId = spreadsheetUrl.split('spreadsheets/d/')[1].split('/')[0];
      }
      
      const response = await api.post(`/integrations/google-sheets/export/${datasetId}`, null, {
        params: { spreadsheet_id: spreadsheetId, sheet_name: DATASETS.find(d => d.id === datasetId)?.name || 'Data' }
      });
      
      addExportLog(datasetId, 'success', `Exported to Google Sheets: ${response.data.records} records`);
      toast.success(`Exported ${response.data.records} records to Google Sheets!`);
    } catch (err) {
      addExportLog(datasetId, 'error', err.response?.data?.detail || 'Export to Sheets failed');
      toast.error(err.response?.data?.detail || 'Failed to export to Google Sheets');
    }
    
    setExporting(prev => ({ ...prev, [`sheets_${datasetId}`]: false }));
  };

  const handleExportDataset = async (datasetId) => {
    setExporting(prev => ({ ...prev, [datasetId]: true }));
    
    const config = exportConfigs[datasetId];
    const dataset = DATASETS.find(d => d.id === datasetId);
    
    try {
      // Fetch data based on dataset type
      let data = [];
      switch (datasetId) {
        case 'patients':
          const patientsRes = await patientsAPI.getAll({});
          data = patientsRes.data;
          break;
        case 'submissions':
          const submissionsRes = await formsAPI.getSubmissions({});
          data = submissionsRes.data;
          break;
        case 'invoices':
          const invoicesRes = await invoicesAPI.getAll({});
          data = invoicesRes.data;
          break;
        case 'event_quotes':
          const quotesRes = await eventQuotesAPI.getAll({});
          data = quotesRes.data;
          break;
        default:
          data = [];
      }

      // Generate CSV
      if (data.length > 0) {
        const headers = config.selectedFields;
        const csvRows = [headers.join(',')];
        
        data.forEach(item => {
          const row = headers.map(field => {
            let value = item[field];
            if (typeof value === 'object') {
              value = JSON.stringify(value);
            }
            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === 'string') {
              value = value.replace(/"/g, '""');
              if (value.includes(',') || value.includes('\n')) {
                value = `"${value}"`;
              }
            }
            return value ?? '';
          });
          csvRows.push(row.join(','));
        });
        
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        downloadBlob(blob, `${datasetId}_export_${new Date().toISOString().split('T')[0]}.csv`);
        
        // Update config with export info
        setExportConfigs(prev => ({
          ...prev,
          [datasetId]: {
            ...prev[datasetId],
            lastExport: new Date().toISOString(),
            lastStatus: 'success',
          }
        }));
        
        // Add to logs
        addExportLog(datasetId, 'success', `Exported ${data.length} records`);
        toast.success(`${dataset.name} exported successfully (${data.length} records)`);
      } else {
        addExportLog(datasetId, 'warning', 'No data to export');
        toast.warning(`No ${dataset.name} data to export`);
      }
    } catch (err) {
      setExportConfigs(prev => ({
        ...prev,
        [datasetId]: {
          ...prev[datasetId],
          lastExport: new Date().toISOString(),
          lastStatus: 'error',
        }
      }));
      addExportLog(datasetId, 'error', err.message || 'Export failed');
      toast.error(`Failed to export ${dataset.name}`);
    }
    
    setExporting(prev => ({ ...prev, [datasetId]: false }));
  };

  const addExportLog = (datasetId, status, message) => {
    const dataset = DATASETS.find(d => d.id === datasetId);
    setExportLogs(prev => [{
      id: Date.now(),
      datasetId,
      datasetName: dataset.name,
      status,
      message,
      timestamp: new Date().toISOString(),
    }, ...prev].slice(0, 50)); // Keep last 50 logs
  };

  const handleExportAll = async () => {
    const enabledDatasets = DATASETS.filter(ds => exportConfigs[ds.id].enabled);
    for (const dataset of enabledDatasets) {
      await handleExportDataset(dataset.id);
    }
    toast.success('All exports completed!');
  };

  const openFieldMapping = (datasetId) => {
    setMappingDataset(DATASETS.find(d => d.id === datasetId));
    setMappingDialogOpen(true);
  };

  const toggleField = (datasetId, field) => {
    setExportConfigs(prev => {
      const current = prev[datasetId].selectedFields;
      const updated = current.includes(field)
        ? current.filter(f => f !== field)
        : [...current, field];
      return {
        ...prev,
        [datasetId]: {
          ...prev[datasetId],
          selectedFields: updated,
        }
      };
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-amber-500" />;
      default:
        return <Clock className="h-4 w-4 text-slate-500" />;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="data-export-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Data Export Center</h1>
          <p className="text-slate-500 mt-1">
            Export EMR data to CSV or sync with Google Sheets
          </p>
        </div>
        <Badge variant="outline" className="gap-1 self-start">
          <Shield className="h-3 w-3" />
          Admin Only
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="export" data-testid="tab-export">
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </TabsTrigger>
          <TabsTrigger value="google-sheets" data-testid="tab-google-sheets">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Google Sheets
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <Activity className="h-4 w-4 mr-2" />
            Export Logs
          </TabsTrigger>
        </TabsList>

        {/* Export Data Tab */}
        <TabsContent value="export" className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Quick Export</CardTitle>
                  <CardDescription>Export all enabled datasets at once</CardDescription>
                </div>
                <Button onClick={handleExportAll} className="gap-2" data-testid="export-all-btn">
                  <Download className="h-4 w-4" />
                  Export All
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Dataset Cards */}
          <div className="grid gap-4">
            {DATASETS.map((dataset) => {
              const config = exportConfigs[dataset.id];
              const Icon = dataset.icon;
              
              return (
                <Card key={dataset.id} data-testid={`dataset-card-${dataset.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-lg bg-primary/10">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{dataset.name}</h3>
                            <Switch
                              checked={config.enabled}
                              onCheckedChange={(checked) => 
                                setExportConfigs(prev => ({
                                  ...prev,
                                  [dataset.id]: { ...prev[dataset.id], enabled: checked }
                                }))
                              }
                            />
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {dataset.description}
                          </p>
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="text-slate-500">
                              {config.selectedFields.length} of {dataset.fields.length} fields selected
                            </span>
                            {config.lastExport && (
                              <span className="flex items-center gap-1">
                                {getStatusIcon(config.lastStatus)}
                                Last: {formatDateTime(config.lastExport)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openFieldMapping(dataset.id)}
                          data-testid={`configure-${dataset.id}`}
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          Fields
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleExportDataset(dataset.id)}
                          disabled={!config.enabled || exporting[dataset.id]}
                          className="gap-1"
                          data-testid={`export-${dataset.id}`}
                        >
                          {exporting[dataset.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                          Export CSV
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Google Sheets Tab */}
        <TabsContent value="google-sheets" className="space-y-6">
          {/* Connection Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Google Sheets Connection
              </CardTitle>
              <CardDescription>
                Connect your Google account to sync data directly to Google Sheets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  {googleStatus.connected ? (
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  ) : (
                    <Unlink className="h-6 w-6 text-slate-500" />
                  )}
                  <div>
                    <p className="font-medium">
                      {googleStatus.connected ? 'Connected to Google' : 'Not Connected'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {googleStatus.connected 
                        ? `OAuth configured: ${googleStatus.configured ? 'Yes' : 'Mock mode'}` 
                        : 'Connect to enable Google Sheets sync'}
                    </p>
                  </div>
                </div>
                {googleStatus.connected ? (
                  <Button variant="outline" onClick={handleDisconnectGoogle}>
                    <Unlink className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                ) : (
                  <Button onClick={handleConnectGoogle} disabled={loadingGoogle}>
                    {loadingGoogle ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link className="h-4 w-4 mr-2" />}
                    Connect Google Account
                  </Button>
                )}
              </div>

              {googleStatus.connected && (
                <>
                  <Separator />
                  
                  {/* Spreadsheet Selection */}
                  <div className="space-y-2">
                    <Label>Destination Spreadsheet ID or URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={spreadsheetUrl}
                        onChange={(e) => setSpreadsheetUrl(e.target.value)}
                        placeholder="Enter Google Sheets URL or ID..."
                        className="flex-1"
                      />
                      {spreadsheetUrl && (
                        <Button variant="outline" asChild>
                          <a href={spreadsheetUrl.startsWith('http') ? spreadsheetUrl : `https://docs.google.com/spreadsheets/d/${spreadsheetUrl}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      Create a new spreadsheet in Google Sheets and paste the URL here
                    </p>
                  </div>

                  {/* Dataset Sync Buttons */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Export Datasets to Sheets</h4>
                    {DATASETS.filter(d => ['patients', 'invoices', 'appointments'].includes(d.id) || d.id === 'submissions').map((dataset) => {
                      const Icon = dataset.icon;
                      const isExporting = exporting[`sheets_${dataset.id}`];
                      
                      return (
                        <div 
                          key={dataset.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-slate-500" />
                            <span className="font-medium">{dataset.name}</span>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleExportToSheets(dataset.id)}
                            disabled={!spreadsheetUrl || isExporting}
                          >
                            {isExporting ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Upload className="h-4 w-4 mr-1" />
                            )}
                            Export to Sheet
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  {!googleStatus.configured && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-900">Setup Required for Full Integration</h4>
                          <p className="text-sm text-amber-800 mt-1">
                            For real-time Google Sheets sync, configure these environment variables:
                          </p>
                          <ol className="text-sm text-amber-800 mt-2 list-decimal list-inside space-y-1">
                            <li>GOOGLE_CLIENT_ID - from Google Cloud Console</li>
                            <li>GOOGLE_CLIENT_SECRET - from Google Cloud Console</li>
                            <li>Enable Google Sheets API in your Google Cloud project</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Export History</CardTitle>
                <CardDescription>Recent export operations and their status</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setExportLogs([])}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Clear Logs
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {exportLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-slate-500 opacity-20" />
                  <p className="text-slate-500">No export logs yet</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Export data to see activity here
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Dataset</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exportLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {getStatusIcon(log.status)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {log.datasetName}
                          </TableCell>
                          <TableCell>{log.message}</TableCell>
                          <TableCell className="text-slate-500">
                            {formatDateTime(log.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Field Mapping Dialog */}
      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Fields: {mappingDataset?.name}</DialogTitle>
            <DialogDescription>
              Select which fields to include in the export
            </DialogDescription>
          </DialogHeader>
          
          {mappingDataset && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {exportConfigs[mappingDataset.id].selectedFields.length} of {mappingDataset.fields.length} selected
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setExportConfigs(prev => ({
                      ...prev,
                      [mappingDataset.id]: {
                        ...prev[mappingDataset.id],
                        selectedFields: mappingDataset.fields
                      }
                    }))}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setExportConfigs(prev => ({
                      ...prev,
                      [mappingDataset.id]: {
                        ...prev[mappingDataset.id],
                        selectedFields: []
                      }
                    }))}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              
              <ScrollArea className="h-[300px] border rounded-lg p-4">
                <div className="space-y-3">
                  {mappingDataset.fields.map((field) => (
                    <div key={field} className="flex items-center space-x-3">
                      <Checkbox
                        id={field}
                        checked={exportConfigs[mappingDataset.id].selectedFields.includes(field)}
                        onCheckedChange={() => toggleField(mappingDataset.id, field)}
                      />
                      <label 
                        htmlFor={field}
                        className="text-sm font-medium cursor-pointer flex-1"
                      >
                        {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </label>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {field}
                      </code>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setMappingDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
